import { KeyboardEvent, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import authService from '../../services/api/auth.service';
import toast from 'react-hot-toast';
import { useAuth } from '../../providers/auth.provider';
import { useNavigate } from 'react-router-dom';
import { FaEdit } from 'react-icons/fa';

const OTPAuth = ({
  phoneNumber,
  onBack,
  setLoginState,
  setAuthToken,
}: {
  phoneNumber: string;
  onBack: () => void;
  setLoginState: (state: 'PHONE' | 'PASSWORD' | 'OTP' | 'REGISTER') => void;
  setAuthToken: (token: string) => void;
}) => {
  const { login, partnerData } = useAuth();
  const [otp, setOtp] = useState('');
  const navigate = useNavigate();

  const onVerifyOTP = async () => {
    try {
      const response = await authService.verifyOTP({
        phone_no: phoneNumber,
        phone_otp: otp,
        domain: partnerData?.company_domain ? partnerData?.company_domain: ''
      });
      toast.success('Login Successful');
      if (response.data.token && response.data.status === "active") {
        login(response.data.token);
        navigate('/');
      } else {
        setAuthToken(response.data.token);
        setLoginState('REGISTER');
      }
    } catch (error: any) {
      toast.error((error as any).response.data.message || 'Something went wrong');
    }
  };

  const resendOTP = async () => {
    try {
      const response = await authService.loginWithOTP({ phone_no: phoneNumber });
      toast.success(response.message);
    } catch (error) {
      toast.error((error as any).response.data.message || 'Something went wrong');
    }
  }


  const handleOtpKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const newOtp = otp.split('');
      newOtp[index - 1] = '';
      setOtp(newOtp.join(''));
      const prevInput = e.currentTarget.previousSibling as HTMLInputElement;
      if (prevInput) prevInput.focus();
    }
  };

  const [timer, setTimer] = useState(30);
  const handleResendOtp = async () => {
    if (timer > 0) return;
    setTimer(30);
    resendOTP();
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [timer]);

  return (
    <div className="space-y-6 px-4 sm:px-6 md:px-8 lg:px-10 ">
      <div className="text-center">
        <p className="text-gray-600 text-sm sm:text-base">Enter the OTP sent to</p>
        <div className="flex justify-center items-center">
          <p className="font-medium text-base sm:text-lg">+91 {phoneNumber}</p>
          <button
            onClick={onBack}
            className="text-primary-800 hover:text-primary-900 transition-colors font-medium text-sm sm:text-base ml-2"
          >
            <FaEdit />
          </button>
        </div>
        <button
          onClick={handleResendOtp}
          disabled={timer > 0}
          className={`${timer > 0 ? 'bg-gray-300' : 'bg-primary-800 hover:bg-primary-900'
            } text-white font-medium rounded-full px-3 py-1 text-sm transition-colors`}
        >
          {timer > 0 ? `Resend OTP in ${timer}s` : 'Resend OTP'}
        </button>

      </div>
      <div className="flex gap-2 sm:gap-3 justify-center">
        {[...Array(6)].map((_, index) => (
          <input
            key={index}
            type="text"
            maxLength={1}
            className="w-12 h-12 sm:w-14 sm:h-14 text-center border-2 border-gray-200 rounded-lg focus:border-primary-700 focus:ring-primary-700 text-lg sm:text-xl font-semibold bg-white/50 transition-all"
            value={otp[index] || ''}
            onChange={(e) => {
              const newOtp = otp.split('');
              newOtp[index] = e.target.value;
              setOtp(newOtp.join(''));
              if (e.target.value && e.target.nextSibling) {
                (e.target.nextSibling as HTMLInputElement).focus();
              }
            }}
            onKeyDown={(e) => handleOtpKeyDown(e, index)}
          />
        ))}
      </div>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onVerifyOTP}
        className="w-full py-3 px-4 bg-primary-800 text-white rounded-lg hover:bg-primary-700 transition-all flex items-center justify-center gap-2 font-medium shadow-lg shadow-primary-700/25"
      >
        Verify OTP
      </motion.button>
    </div>
  );
};

export default OTPAuth;
